import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import {
    Search, Share2, X, MoreVertical, ArrowLeft, Trash2, Pencil,
    Maximize2, Minimize2, CheckSquare, ListChecks, Check,
} from 'lucide-react';
import PanelShell from './PanelShell';
import ChatMessages from '../ChatMessages';
import ChatInput from '../ChatInput';
import AttachPickerModal from '../AttachPickerModal';
import ShareDialog from '../ShareDialog';
import Modal from '../ui/Modal';
import RemindersDropdown from '../RemindersDropdown';
import apiService from '../../services/apiService';
import { documentService } from '../../services/documentService';
import { mapDbMessages } from '../../utils/messageMapper';
import { parseServerDate } from '../../utils/datetime';
import { debounce } from '../../utils/debounce';
import { composeSnapshot } from '../../utils/composeSnapshot';
import { isDocumentFile } from '../../utils/constants';
import { logger } from '../../utils/logger';
import { useLanguage } from '../../i18n';
import useAuth from '../../hooks/useAuth';
import { useApiStatus } from '../../hooks/useApiStatus';
import { useComposerVoiceSegments } from '../../hooks/useComposerVoiceSegments';
import { makeAttachmentDescriptor } from '../../hooks/useFileAttachment';
import { useIsMobile } from '../../hooks/useIsMobile';
import { avatarInitials } from '../../utils/avatar';
import { COMPACT_BELOW, gridColumnsFor } from '../../utils/dayChannelGrid';
import { streamErrorFromError, streamErrorText, userErrorMessage } from '../../utils/userError';

/**
 * Day-anchored channel panel — Library grid + Focus workspace, shared by the
 * day-anchored channels (today: Telegram only — #530 hid the other channel in
 * the web app; behavior is driven entirely by the channel config).
 *
 * A DayBlock is a channel-tagged Conversation bucketed to a user-local day.
 * It is a STANDARD workspace module (Telegram dual display #444): it opens in
 * the split slot beside the chat or a paired feature, expands only when the user
 * asks, and opening a day never changes the panel's footprint.
 *
 * The Library grid shows one card per day (up to 5 per row, container-driven —
 * fewer in a narrow split column) in a viewport that
 * FILLS the panel's available height (#253 — it used to be capped at "two rows +
 * a ⅓ peek", which sliced the third row and left dead space below), so as many
 * complete rows as fit are shown and the history scrolls naturally inside it.
 * Clicking a card opens the Focus workspace — the
 * SAME conversation thread rendered with the standard chat stack, continued via
 * the normal RAG send path (isolation is BE-structural; a web continuation is
 * never pushed back to the phone).
 *
 * The "No-Reset" rule is honoured structurally: the grid stays MOUNTED while a
 * day is focused (it's merely hidden with `hidden`), so the search string, the
 * filtered `days`, and the exact grid scroll position are all preserved in the
 * DOM/state — closing the focus view restores the grid with zero refetch flash
 * and zero scroll jump.
 *
 * `cfg` (a stable module-level object in each wrapper) parametrizes:
 *   key         — channel key; prefixes every data-testid ('telegram-…')
 *   i18nPrefix  — prefixes every channel string key ('telegramItems', …)
 *   titleKey    — the panel title i18n key
 *   Icon        — lucide icon component
 *   accent      — the channel's accent hex; falls back to CHANNEL_ACCENT[key]
 *   iconClass / dotClass / connectedClass — LEGACY brand colour classes, no
 *                 longer read (the accent above replaced them, and the
 *                 Connected line is always green)
 *   service     — { listDays({search}), renameDay(id, title), deleteDay(id) }
 */

// Fixed card metrics make the ⅓-row peek deterministic (and E2E-assertable):
// the scroll viewport is exactly 2 full rows + 2 gaps + ⅓ of a third row.
const CARD_H = 150;      // px — fixed card height
const GRID_GAP = 16;     // px — gap between rows/columns (Tailwind gap-4)
// Container-driven column count + compact threshold (Telegram dual display
// #444) live in utils/dayChannelGrid so they can be unit-tested.
// (The old PEEK_HEIGHT — a hard "2 rows + ⅓" ceiling on the grid viewport — was
// removed for #253: it sliced the third row of buckets and left dead space below.
// The grid now fills the panel's available height; see the grid viewport below.)

// One component, two identities: the channel accent tints the header badge, the
// day-card marker and the channel icon — a 1.5px border and an icon colour, per
// the accent rule, never a filled surface.
//
// The CONNECTED status line is deliberately NOT accented: it is always green,
// because it reports a connection state, not a brand. `cfg.dotClass` /
// `cfg.connectedClass` are therefore no longer read.
const CHANNEL_ACCENT = {
    telegram: '#4A90F0',
};
const accentOf = (cfg) => cfg.accent || CHANNEL_ACCENT[cfg.key] || '#ffffff';

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const ordinal = (d) => {
    const rem100 = d % 100;
    if (rem100 >= 11 && rem100 <= 13) return 'th';
    switch (d % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
    }
};

// "May 27th" from a 'YYYY-MM-DD' day_date. Parsed as a LOCAL calendar date (not
// via new Date('YYYY-MM-DD'), which is UTC-midnight and can shift the day).
function formatDayDate(dayDate) {
    if (!dayDate) return '';
    const m = String(dayDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
    let dt;
    if (m) dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    else dt = parseServerDate(dayDate);
    if (!dt || Number.isNaN(dt.getTime())) return String(dayDate);
    const day = dt.getDate();
    return `${MONTHS[dt.getMonth()]} ${day}${ordinal(day)}`;
}

// "just now" / "16h ago" / "3d ago", localized through ct().
function relativeAgo(iso, ct) {
    const d = parseServerDate(iso);
    if (!d) return '';
    const diffMs = Date.now() - d.getTime();
    const min = Math.max(0, Math.floor(diffMs / 60000));
    if (min < 1) return ct('AgoNow');
    if (min < 60) return ct('AgoMin', { n: min });
    const hr = Math.floor(min / 60);
    if (hr < 24) return ct('AgoHour', { n: hr });
    const day = Math.floor(hr / 24);
    return ct('AgoDay', { n: day });
}

// ── Day card ─────────────────────────────────────────────────────────

// Rough popover height (5 rows + a divider) — used only to decide up-vs-down
// placement so the box is always fully framed on screen.
const MENU_EST_HEIGHT = 216;

const DayCard = ({
    cfg, day, dateLabel, onOpen, onRename, onShare, onDelete, ct, t,
    // Selection model telegram (#443): `selectMode` turns a click into a toggle
    // and shows the checkbox; `onSelect`/`onSelectAll` are the menu entries.
    selectMode = false, selected = false, onToggleSelect, onSelect, onSelectAll,
}) => {
    const [menuOpen, setMenuOpen] = useState(false);
    // Portal-anchored menu position in viewport (fixed) coords. Either `top` OR
    // `bottom` is set (open below or above the trigger) — see openMenu().
    const [menuPos, setMenuPos] = useState(null);
    const [renaming, setRenaming] = useState(false);
    const [draft, setDraft] = useState(day.title || '');
    const menuBtnRef = useRef(null);
    const popoverRef = useRef(null);
    const inputRef = useRef(null);

    // Close on outside click, and on scroll/resize so the portaled box never
    // detaches from a moving trigger (the grid viewport is scrollable). The menu
    // lives in a document.body portal, so the outside-click test must consult
    // BOTH the trigger and the portal node, not a single wrapper ref.
    useEffect(() => {
        if (!menuOpen) return undefined;
        const onDown = (e) => {
            if (menuBtnRef.current?.contains(e.target)) return;
            if (popoverRef.current?.contains(e.target)) return;
            setMenuOpen(false);
        };
        const onReflow = () => setMenuOpen(false);
        document.addEventListener('mousedown', onDown);
        window.addEventListener('resize', onReflow);
        window.addEventListener('scroll', onReflow, true); // capture: any ancestor scroll
        return () => {
            document.removeEventListener('mousedown', onDown);
            window.removeEventListener('resize', onReflow);
            window.removeEventListener('scroll', onReflow, true);
        };
    }, [menuOpen]);

    useEffect(() => {
        if (renaming) {
            setDraft(day.title || '');
            // focus after paint
            setTimeout(() => inputRef.current?.select?.(), 0);
        }
    }, [renaming, day.title]);

    // Anchor the popover to the trigger via getBoundingClientRect + a body
    // portal, so the card's `overflow-hidden` + fixed height can NEVER clip it
    // (BUG: Bucket Menu Clipping). Right-aligned to the trigger; opens
    // downward when there's room, upward otherwise → always fully visible.
    const openMenu = (e) => {
        e.stopPropagation();
        if (menuOpen) { setMenuOpen(false); return; }
        const r = menuBtnRef.current?.getBoundingClientRect();
        if (r) {
            const right = Math.max(8, Math.round(window.innerWidth - r.right));
            const roomBelow = window.innerHeight - r.bottom;
            const pos = roomBelow >= MENU_EST_HEIGHT + 8
                ? { top: Math.round(r.bottom + 4), right }
                : { bottom: Math.round(window.innerHeight - r.top + 4), right };
            setMenuPos(pos);
        }
        setMenuOpen(true);
    };

    const commitRename = () => {
        const clean = (draft || '').trim();
        setRenaming(false);
        if (clean && clean !== day.title) onRename(day.id, clean);
    };

    const count = day.message_count ?? 0;
    const accent = accentOf(cfg);

    return (
        <div
            data-testid={`${cfg.key}-day-card`}
            data-day-id={day.id}
            data-selected={selectMode ? (selected ? 'true' : 'false') : undefined}
            onClick={() => {
                if (renaming) return;
                if (selectMode) onToggleSelect?.(day);
                else onOpen(day);
            }}
            style={{ height: CARD_H }}
            className={`group relative flex flex-col rounded-bubble border bg-white/[0.03] p-[18px] cursor-pointer transition-colors hover:bg-white/[0.08] overflow-hidden ${
                selected ? 'border-white/[0.55] bg-white/[0.08]' : 'border-pai hover:border-white/[0.26]'
            }`}
        >
            {/* Selection checkbox (#443) — the File Manager "Select Mission"
                pattern: every card shows one while selection mode is on, and the
                whole card is the hit target (the box is a visible marker that is
                also independently clickable/keyboard-operable). */}
            {selectMode && (
                <button
                    type="button"
                    role="checkbox"
                    aria-checked={selected}
                    aria-label={ct('CardCheckboxLabel', { date: dateLabel })}
                    data-testid={`${cfg.key}-card-checkbox`}
                    data-checked={selected ? 'true' : 'false'}
                    onClick={(e) => { e.stopPropagation(); onToggleSelect?.(day); }}
                    className={`absolute top-3 right-3 z-10 w-6 h-6 flex items-center justify-center rounded-[6px] border-[1.5px] transition-colors ${
                        selected ? 'bg-white border-white text-black' : 'bg-black/30 border-white/[0.55] text-transparent'
                    }`}
                >
                    <Check size={15} strokeWidth={3} />
                </button>
            )}
            {/* The date row: an 18px accent ring, then the day. */}
            <div className="flex items-center gap-2.5 min-w-0">
                <span
                    aria-hidden="true"
                    className="w-[18px] h-[18px] rounded-full border-[1.5px] flex-shrink-0"
                    style={{ borderColor: accent }}
                />
                <div
                    data-testid={`${cfg.key}-card-date`}
                    className="text-[15.5px] font-bold leading-tight text-white truncate min-w-0"
                >
                        {dateLabel}
                    </div>
            </div>
            {/* Indented to sit under the DATE, not under the ring. */}
            <div
                data-testid={`${cfg.key}-card-count`}
                className="text-[12.5px] leading-tight text-pai-meta tnum ml-[28px] mt-[7px] truncate"
            >
                        {ct('Items', { n: count })}
                    </div>

            {/* The margins here are tuned so a TWO-line topic still fits inside
                the fixed 150px card (CARD_H, which the library E2E measures). */}
            <div className="mt-2 flex-1 min-h-0 min-w-0">
                {renaming ? (
                    <input
                        ref={inputRef}
                        data-testid={`${cfg.key}-rename-input`}
                        value={draft}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Enter') commitRename();
                            else if (e.key === 'Escape') setRenaming(false);
                        }}
                        onBlur={commitRename}
                        className="pai-input pai-glow w-full px-2.5 py-1.5 text-[14px]"
                    />
                ) : (
                    <div
                        data-testid={`${cfg.key}-card-summary`}
                        className="text-[14.5px] font-semibold leading-[1.35] text-pai-body line-clamp-2 break-words"
                    >
                        {day.title || t('newChat')}
                    </div>
                )}
            </div>

            <div
                data-testid={`${cfg.key}-card-updated`}
                className="mt-1.5 text-[12.5px] leading-tight text-pai-faint truncate"
            >
                {ct('Updated', { ago: relativeAgo(day.updated_at || day.last_activity, ct) })}
            </div>

            {/* Hover-only ⋯ trigger, bottom-right. The menu itself is PORTALED to
                <body> (below), so the card's `overflow-hidden` clip + fixed
                150px height can never trap or cut it off. */}
            {!selectMode && (
            <div
                className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 pai-touch-reveal transition-opacity"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    ref={menuBtnRef}
                    data-testid={`${cfg.key}-card-menu`}
                    aria-label={t('auxDayOptions', 'Day options')}
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    onClick={openMenu}
                    className="pai-icon-btn w-8 h-8"
                >
                    <MoreVertical size={16} />
                </button>
            </div>
            )}

            {/* Floating pop-over rendered at <body> — never clipped by the card.
                Right-aligned to the trigger; opens up or down (menuPos) to stay
                fully on screen. Events bubble the REACT tree (portal quirk), so
                stopPropagation here keeps a menu click from opening the day. */}
            {menuOpen && menuPos && createPortal(
                <div
                    ref={popoverRef}
                    role="menu"
                    data-testid={`${cfg.key}-card-menu-popover`}
                    onClick={(e) => e.stopPropagation()}
                    style={{ position: 'fixed', top: menuPos.top, bottom: menuPos.bottom, right: menuPos.right }}
                    className="z-[90] w-52 bg-pai-modal border border-pai rounded-ctl shadow-modal-sm py-1.5"
                >
                    {/* Selection model telegram (#443): enter selection mode with
                        this day ticked, or with every listed day ticked. */}
                    <button
                        data-testid={`${cfg.key}-card-select`}
                        role="menuitem"
                        onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onSelect?.(day); }}
                        className="w-full flex items-center gap-2.5 px-3.5 py-2 text-[14px] font-medium text-pai-secondary hover:bg-white/[0.07] hover:text-white transition-colors"
                    >
                        <CheckSquare size={14} /> {ct('SelectAction')}
                    </button>
                    <button
                        data-testid={`${cfg.key}-card-select-all`}
                        role="menuitem"
                        onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onSelectAll?.(); }}
                        className="w-full flex items-center gap-2.5 px-3.5 py-2 text-[14px] font-medium text-pai-secondary hover:bg-white/[0.07] hover:text-white transition-colors"
                    >
                        <ListChecks size={14} /> {ct('SelectAllAction')}
                    </button>
                    <div role="separator" className="my-1.5 border-t border-pai-hairline" />
                    <button
                        data-testid={`${cfg.key}-card-rename`}
                        role="menuitem"
                        onClick={(e) => { e.stopPropagation(); setMenuOpen(false); setRenaming(true); }}
                        className="w-full flex items-center gap-2.5 px-3.5 py-2 text-[14px] font-medium text-pai-secondary hover:bg-white/[0.07] hover:text-white transition-colors"
                    >
                        <Pencil size={14} /> {ct('RenameAction')}
                    </button>
                    <button
                        data-testid={`${cfg.key}-card-share`}
                        role="menuitem"
                        onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onShare(day); }}
                        className="w-full flex items-center gap-2.5 px-3.5 py-2 text-[14px] font-medium text-pai-secondary hover:bg-white/[0.07] hover:text-white transition-colors"
                    >
                        <Share2 size={14} /> {ct('ShareAction')}
                    </button>
                    <button
                        data-testid={`${cfg.key}-card-delete`}
                        role="menuitem"
                        onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDelete(day); }}
                        className="w-full flex items-center gap-2.5 px-3.5 py-2 text-[14px] font-medium text-pai-red hover:bg-pai-red/[0.12] transition-colors"
                    >
                        <Trash2 size={14} /> {ct('DeleteAction')}
                    </button>
                </div>,
                document.body,
            )}
        </div>
    );
};

// ── Delete confirmation modal (EXACT ticket text) ────────────────────

const DeleteDayModal = ({ cfg, dateLabel, onConfirm, onCancel, ct }) => {
    return (
        <Modal onClose={onCancel} zIndex="z-[80]" cardClassName="p-8" cardTestId={`${cfg.key}-delete-modal`}>
                <p className="text-[15px] leading-[1.6] text-pai-body mb-7">
                    {ct('DeletePrompt', { date: dateLabel })}
                </p>
                <div className="flex items-center gap-2.5">
                    <button
                        data-testid={`${cfg.key}-delete-yes`}
                        onClick={onConfirm}
                        className="pai-btn-danger flex-1 h-11"
                    >
                        {ct('DeleteYes')}
                    </button>
                    <button
                        data-testid={`${cfg.key}-delete-no`}
                        onClick={onCancel}
                        className="pai-btn-secondary flex-1 h-11"
                    >
                        {ct('DeleteNo')}
                    </button>
                </div>
            </Modal>
    );
};

// ── Bulk delete confirmation (Selection model telegram #443) ─────────

const BulkDeleteModal = ({ cfg, count, onConfirm, onCancel, ct }) => {
    // <Modal>'s Escape is capture phase + stopPropagation: PanelShell also
    // closes on Escape, and dismissing this dialog must not take the whole
    // panel down with it (the #272 lesson).
    return (
        <Modal onClose={onCancel} zIndex="z-[80]" cardClassName="p-8" cardTestId={`${cfg.key}-bulk-delete-modal`}>
                <p className="text-[15px] leading-[1.6] text-pai-body mb-7">
                    {ct('BulkDeletePrompt', { n: count })}
                </p>
                <div className="flex items-center gap-2.5">
                    <button
                        data-testid={`${cfg.key}-bulk-delete-yes`}
                        onClick={onConfirm}
                        className="pai-btn-danger flex-1 h-11"
                    >
                        {ct('BulkDeleteYes')}
                    </button>
                    <button
                        data-testid={`${cfg.key}-bulk-delete-no`}
                        onClick={onCancel}
                        className="pai-btn-secondary flex-1 h-11"
                    >
                        {ct('BulkDeleteNo')}
                    </button>
                </div>
            </Modal>
    );
};

// ── Focus workspace (full-screen day thread) ─────────────────────────

/**
 * The Bucket Day workspace — a professional, Main-Office-grade chat surface over
 * ONE day's conversation (#173 "Full Tool Parity", uni-directional).
 *
 * GROUND TRUTH = the Main Chat. Rather than re-implementing a lesser composer,
 * this view REUSES the real components: <ChatMessages>/<Message> (so the reply
 * arrow, the edit pencil, voice bubbles, attachments, timestamps and the
 * ThinkingSteps timer header are byte-identical) and <ChatInput> (so the "+"
 * menu, the multi-clip voice tray, the reply pill, the recording bar and the
 * Stop control are byte-identical). Only the per-conversation STATE machinery
 * lives here — deliberately a lean version of MainOfficeView's, because a bucket
 * is bound to exactly ONE conversation: no conversation switching, therefore no
 * drafts, no LRU message buffers, no origin-bound upload binding.
 *
 * 🛑 STRUCTURAL ISOLATION (non-negotiable): everything done here stays in the
 * Main Office. This module talks to the ordinary conversation + RAG endpoints
 * ONLY — it imports no channel service and there is no code path from here to
 * a channel service. Outbound channel sends happen
 * exclusively in the BE webhook path, so a web recording / edit / reply is never
 * pushed back to the user's phone.
 */
const FocusView = ({
    cfg, day, dateLabel, onClose, onShare, userInitials, apiStatus, ct, initialSearch = '',
    mode = 'split', onToggleExpand, compact = false,
}) => {
    // `ct` (passed in) resolves CHANNEL-prefixed keys — telegramNoDays vs. a
    // generic key. `t` is the generic dictionary for chrome that is identical on
    // both channels (Close, Share conversation, …), so both are needed here.
    const { t } = useLanguage();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [attachedFiles, setAttachedFiles] = useState([]);
    const [replyingToMessages, setReplyingToMessages] = useState([]);
    const [highlightedMessageId, setHighlightedMessageId] = useState(null);
    const [sending, setSending] = useState(false);
    // Send pressed while a clip is still transcribing → flush automatically once
    // every transcription settles (the Main Office "wait-and-send" contract).
    const [pendingSend, setPendingSend] = useState(false);
    const [docPickerOpen, setDocPickerOpen] = useState(false);
    // Seeded when the day was opened from a MESSAGE-level global-search hit
    // ("Conversation misplaced" deep link, /<channel>?day=…&q=…): the in-day
    // search opens primed, so the matched line is highlighted instead of leaving
    // the user at the bottom of a long transcript. A day opened normally from the
    // grid passes no initialSearch and is untouched.
    const [showSearch, setShowSearch] = useState(!!initialSearch);
    const [daySearch, setDaySearch] = useState(initialSearch || '');
    const [matchCount, setMatchCount] = useState(null);
    // The day bucket IS the conversation, so a clip recorded here is attributed
    // to it on the Energy Receipt's Transcription line (#377).
    const voice = useComposerVoiceSegments(day.id);
    // Live mirror of `messages` so turn assembly / jump-to can READ the current
    // thread without abusing a setState updater as a getter (that updater runs in
    // the render phase and twice under StrictMode).
    const messagesRef = useRef(messages);
    useEffect(() => { messagesRef.current = messages; }, [messages]);
    const messagesEndRef = useRef(null);
    const threadRef = useRef(null);
    const stopFnRef = useRef(null);
    const uploadAbortersRef = useRef({});
    const { Icon } = cfg;
    const accent = accentOf(cfg);

    const load = useCallback(async () => {
        try {
            const conv = await apiService.getConversation(day.id);
            setMessages(mapDbMessages(conv));
        } catch (_) { /* keep whatever's there */ }
    }, [day.id]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // In-day search reuses GET /conversations/<id>/messages/search; the visual
    // highlight rides the existing ChatMessages searchQuery mechanism.
    const runDaySearch = useCallback(async (q) => {
        if (!q.trim()) { setMatchCount(null); return; }
        try {
            const res = await apiService.searchMessagesInConversation(day.id, q.trim());
            setMatchCount((res?.results || res?.matches || []).length ?? 0);
        } catch (_) { setMatchCount(null); }
    }, [day.id]);

    // Deep-linked in-day search: resolve the match count once, on mount.
    useEffect(() => {
        if (initialSearch) runDaySearch(initialSearch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Attachments (composer "+" → Upload from Device / Select from FM) ──
    const uploadOne = useCallback(async (file, folderId, tempId) => {
        const aborter = new AbortController();
        uploadAbortersRef.current[tempId] = aborter;
        try {
            // #404 — the day bucket IS a conversation, so an attached image's OCR
            // is billed to it rather than landing unattributed.
            const result = await documentService.uploadDocumentToFolder(
                file, folderId, null, null, aborter.signal, day?.id,
            );
            const doc = result?.document || result?.documents?.[0] || result;
            if (!doc?.id) throw new Error('Upload did not return a document id');
            setAttachedFiles((prev) => prev.map((f) => (f.tempId === tempId ? {
                ...f,
                id: doc.id,
                filename: doc.original_filename || doc.filename || file.name,
                mime_type: doc.mime_type || file.type,
                file_size: doc.file_size ?? file.size,
                is_image: (doc.mime_type || file.type || '').startsWith('image/'),
                uploading: false,
                upload_error: null,
            } : f)));
        } catch (err) {
            if (err?.aborted || aborter.signal.aborted) return; // pill already removed
            logger.error('UPLOAD', `Bucket upload failed: ${file.name}`, err);
            setAttachedFiles((prev) => prev.map((f) => (f.tempId === tempId
                ? { ...f, uploading: false, upload_error: userErrorMessage(err) }
                : f)));
        } finally {
            delete uploadAbortersRef.current[tempId];
        }
    // day?.id: #404 threads it into the upload, so a stale closure would
    // silently go back to unattributed OCR.
    }, [day?.id]);

    const handleAddFiles = useCallback(async (files) => {
        const supported = [...(files || [])].filter((f) => isDocumentFile(f.name));
        if (supported.length === 0) return;
        let folderId = null;
        try {
            folderId = await documentService.ensureSystemFolder('conversation-uploads');
        } catch (err) {
            logger.error('UPLOAD', 'Failed to ensure Conversation Uploads folder:', err);
        }
        for (const file of supported) {
            const pending = makeAttachmentDescriptor(file);
            setAttachedFiles((prev) => [...prev, pending]);
            await uploadOne(file, folderId, pending.tempId);
        }
    }, [uploadOne]);

    // Attach an EXISTING document without re-uploading (a completed pill).
    const handleSelectExistingDocument = useCallback((docId, doc) => {
        const row = doc || {};
        const id = docId || row.id;
        if (!id) return;
        setAttachedFiles((prev) => {
            if (prev.some((f) => f.id === id)) return prev;
            const mime = row.mime_type || 'application/octet-stream';
            return [...prev, {
                tempId: `att-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                id,
                filename: row.original_filename || row.filename || 'Document',
                mime_type: mime,
                file_size: row.file_size ?? 0,
                is_image: mime.startsWith('image/') || !!row.is_image,
                uploading: false,
                upload_error: null,
            }];
        });
    }, []);

    const handleRemoveAttachment = useCallback((descriptor) => {
        const tempId = descriptor?.tempId;
        if (tempId && uploadAbortersRef.current[tempId]) {
            try { uploadAbortersRef.current[tempId].abort(); } catch (_) { /* noop */ }
            delete uploadAbortersRef.current[tempId];
        }
        setAttachedFiles((prev) => prev.filter((f) => (
            tempId ? f.tempId !== tempId : f.id !== descriptor?.id
        )));
    }, []);

    const handleClearAttachments = useCallback(() => {
        Object.values(uploadAbortersRef.current).forEach((a) => {
            try { a.abort(); } catch (_) { /* noop */ }
        });
        uploadAbortersRef.current = {};
        setAttachedFiles([]);
    }, []);

    // ── Turn execution ───────────────────────────────────────────────────
    // ONE runner for every turn: a fresh send AND an edit re-run (skipUserMessage).
    // It is the SAME rag-query-stream path the Main Office uses, with
    // conversation_id = this day bucket; documentService injects client_timezone
    // (#125 reference tz) + client_now, so time-sensitive turns resolve exactly as
    // they do in the Main Chat. Nothing here reaches a channel service.
    const runTurn = useCallback(async ({
        backendContent,
        displayContent,
        attachedDocuments = null,
        dispatchSegments = null,
        audioTranscriptions = null,
        typed = null,
        replyingTo = null,
        skipUserMessage = false,
    }) => {
        const turnStartMs = Date.now();
        setSending(true);

        const repliedToData = replyingTo && replyingTo.length > 0
            ? replyingTo.map((msg) => ({
                id: msg.dbId || null,
                role: msg.isUser ? 'user' : 'assistant',
                content: msg.display || msg.content,
                attached_documents: msg.attachedDocuments || msg.metadata?.attached_documents || null,
            }))
            : null;

        // Prior history BEFORE the optimistic user turn is appended.
        const priorMessages = messagesRef.current;

        const userMessageId = `u-${Date.now()}`;
        // #446: the stored USER message this run answers — what Stop cancels
        // server-side (documentService fires POST …/cancel-run on stop/abort).
        let streamUserDbId = skipUserMessage
            ? ([...priorMessages].reverse().find((m) => m.isUser && m.dbId)?.dbId || null)
            : null;
        if (!skipUserMessage) {
            const userMessage = {
                id: userMessageId,
                content: displayContent,
                timestamp: new Date(),
                isUser: true,
                attachedDocuments: attachedDocuments?.length ? attachedDocuments : undefined,
                repliedTo: repliedToData,
                voiceSegments: dispatchSegments?.length ? dispatchSegments : undefined,
                typedText: typed || null,
            };
            setMessages((prev) => [...prev, userMessage]);

            const messageMetadata = {};
            if (repliedToData) messageMetadata.repliedTo = repliedToData;
            if (attachedDocuments?.length) messageMetadata.attached_documents = attachedDocuments;
            if (dispatchSegments?.length) messageMetadata.voice_segments = dispatchSegments;
            if (typed) messageMetadata.typed_text = typed;
            try {
                const saved = await apiService.addMessageToConversation(
                    day.id,
                    displayContent,
                    'user',
                    Object.keys(messageMetadata).length > 0 ? messageMetadata : null,
                );
                if (saved?.id) {
                    streamUserDbId = saved.id;
                    setMessages((prev) => prev.map((m) => (m.id === userMessageId ? { ...m, dbId: saved.id } : m)));
                }
            } catch (err) {
                logger.error('CHAT', 'Bucket: error saving user message', err);
            }
        }

        const conversationContext = priorMessages.map((m) => ({
            id: m.dbId || null,   // #504: resolved-card outcomes are looked up by id
            role: m.isUser ? 'user' : 'assistant',
            content: m.content,
            attached_documents: m.attachedDocuments || m.metadata?.attached_documents || null,
        }));

        const assistantId = `a-${Date.now()}`;
        setMessages((prev) => [...prev, {
            id: assistantId,
            content: '',
            timestamp: new Date(),
            isUser: false,
            isSearching: true,
            searchingText: '💭 Analyzing your request...',
            procStartMs: turnStartMs,
        }]);

        let full = '';
        let stopped = false;
        let processingMs = null;
        const liveSteps = [];
        const abortController = new AbortController();
        stopFnRef.current = () => {
            stopped = true;
            abortController.abort();
            setSending(false);
            stopFnRef.current = null;
            setMessages((prev) => prev.map((m) => (m.id === assistantId
                ? { ...m, content: full || '*(Stopped)*', isSearching: false } : m)));
        };

        const pushStep = (step) => {
            liveSteps.push(step);
            setMessages((prev) => prev.map((m) => (m.id === assistantId
                ? { ...m, toolSteps: [...liveSteps] } : m)));
        };

        try {
            const stopFunction = await documentService.ragQueryStream(
                backendContent,
                {
                    conversation_id: day.id,
                    conversation_context: conversationContext,
                    attached_documents: attachedDocuments?.length ? attachedDocuments : null,
                    // `id` lets the BE resolve the quote's media server-side (#386).
                    replied_to_messages: replyingTo && replyingTo.length > 0
                        ? replyingTo.map((msg) => ({
                            id: msg.dbId || null,
                            role: msg.isUser ? 'user' : 'assistant',
                            content: msg.content,
                            attached_documents: msg.attachedDocuments || msg.metadata?.attached_documents || null,
                        }))
                        : null,
                    text_content: typed || null,
                    audio_transcriptions: audioTranscriptions?.length ? audioTranscriptions : null,
                    user_message_id: streamUserDbId,
                },
                (token) => {
                    if (stopped) return;
                    if (processingMs === null) processingMs = Date.now() - turnStartMs;
                    full += token;
                    setMessages((prev) => prev.map((m) => (m.id === assistantId
                        ? { ...m, content: full, isSearching: false, processingMs } : m)));
                },
                async (metadata) => {
                    if (stopped) return;
                    // ⚠️ Yield 100ms before touching the network again. documentService
                    // cancels the SSE reader the instant [DONE] arrives, and issuing the
                    // next request to this origin in the SAME tick lands it on the
                    // tearing-down keep-alive socket: Chrome reports the request as sent,
                    // the server never receives it, and the promise never settles (the
                    // turn would hang forever with the Stop button stuck on). The Main
                    // Office's onComplete opens with the identical yield — this is
                    // parity, not superstition.
                    await new Promise((resolve) => setTimeout(resolve, 100));
                    if (processingMs === null) processingMs = Date.now() - turnStartMs;
                    // #439: an error chunk arrives as metadata.stream_error (never
                    // as answer text) — same record + notice as the Main Office.
                    const streamError = metadata?.stream_error
                        ? { ...metadata.stream_error, content_is_error: !full.trim() }
                        : null;
                    if (streamError && !full.trim()) full = streamErrorText(streamError);
                    const finalMeta = {
                        ...(metadata || {}), toolSteps: [...liveSteps], processing_ms: processingMs,
                        ...(streamError ? { stream_error: streamError } : {}),
                    };
                    setMessages((prev) => prev.map((m) => (m.id === assistantId
                        ? {
                            ...m,
                            content: full || '…',
                            isSearching: false,
                            metadata: finalMeta,
                            toolSteps: [...liveSteps],
                            processingMs,
                        }
                        : m)));
                    try {
                        const saved = await apiService.addMessageToConversation(day.id, full, 'assistant', finalMeta);
                        if (saved?.id) {
                            setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, dbId: saved.id } : m)));
                        }
                    } catch (err) {
                        logger.error('CHAT', 'Bucket: error saving assistant message', err);
                    }
                    setSending(false);
                    stopFnRef.current = null;
                },
                (err) => {
                    const isAbort = err?.name === 'AbortError' || /abort/i.test(err?.message || '');
                    if (stopped || isAbort) { setSending(false); stopFnRef.current = null; return; }
                    const streamError = streamErrorFromError(err, { contentIsError: !full.trim() });
                    setMessages((prev) => prev.map((m) => (m.id === assistantId
                        ? {
                            ...m,
                            content: full || streamErrorText(streamError),
                            isSearching: false,
                            metadata: { ...(m.metadata || {}), stream_error: streamError },
                        }
                        : m)));
                    setSending(false);
                    stopFnRef.current = null;
                },
                (payload) => {
                    if (stopped || typeof payload !== 'object' || payload.type !== 'tool_status') return;
                    if (payload.event === 'tool_start') {
                        pushStep({
                            id: `${payload.tool}_${payload.iteration}_${Date.now()}`,
                            tool: payload.tool,
                            argsSummary: payload.args_summary || '',
                            resultSummary: '',
                            durationMs: 0,
                            iteration: payload.iteration,
                            status: 'running',
                        });
                    } else if (payload.event === 'thinking') {
                        pushStep({
                            id: `thinking_${payload.iteration}_${Date.now()}`,
                            tool: 'thinking',
                            argsSummary: payload.args_summary || '',
                            resultSummary: '',
                            durationMs: 0,
                            iteration: payload.iteration,
                            status: 'done',
                        });
                    } else if (payload.event === 'tool_complete') {
                        for (let i = liveSteps.length - 1; i >= 0; i--) {
                            if (liveSteps[i].tool === payload.tool
                                && liveSteps[i].iteration === payload.iteration
                                && liveSteps[i].status === 'running') {
                                liveSteps[i] = {
                                    ...liveSteps[i],
                                    resultSummary: payload.result_summary || '',
                                    durationMs: payload.duration_ms || 0,
                                    status: 'done',
                                };
                                break;
                            }
                        }
                        setMessages((prev) => prev.map((m) => (m.id === assistantId
                            ? { ...m, toolSteps: [...liveSteps] } : m)));
                    }
                },
                abortController.signal,
            );
            // Stop must abort the fetch AND the SSE reader loop.
            const abortOnly = stopFnRef.current;
            stopFnRef.current = () => { if (stopFunction) stopFunction(); abortOnly?.(); };
        } catch (err) {
            logger.error('CHAT', 'Bucket: RAG setup failed', err);
            const streamError = streamErrorFromError(err, { contentIsError: true });
            setMessages((prev) => prev.map((m) => (m.id === assistantId
                ? {
                    ...m,
                    content: streamErrorText(streamError),
                    isSearching: false,
                    metadata: { ...(m.metadata || {}), stream_error: streamError },
                } : m)));
            setSending(false);
            stopFnRef.current = null;
        }
    }, [day.id]);

    // ── Send (text + N voice clips + attachments + replies, in ONE turn) ──
    const dispatchSnapshot = useCallback((snap) => {
        if (!snap) return;
        setInput('');
        setAttachedFiles([]);
        setReplyingToMessages([]);
        voice.clear();
        runTurn({
            backendContent: snap.backendContent,
            displayContent: snap.displayContent,
            attachedDocuments: snap.attachedDocuments,
            dispatchSegments: snap.dispatchSegments,
            audioTranscriptions: snap.audioTranscriptions,
            typed: snap.typed || null,
            replyingTo: snap.replyingTo,
        });
    }, [runTurn, voice]);

    const buildSnapshot = useCallback(() => composeSnapshot({
        input, attachedFiles, voiceSegments: voice.segments, replyingToMessages,
    }), [input, attachedFiles, voice.segments, replyingToMessages]);

    const handleSendMessage = useCallback(() => {
        if (sending || pendingSend) return;
        if (attachedFiles.some((f) => f.uploading)) return;
        const snap = buildSnapshot();
        if (!snap) return;
        // Wait-and-send: queue until every clip's transcription has settled.
        if (voice.hasTranscribing) { setPendingSend(true); return; }
        dispatchSnapshot(snap);
    }, [sending, pendingSend, attachedFiles, buildSnapshot, voice.hasTranscribing, dispatchSnapshot]);

    // Flush a queued send the moment the last transcription settles. Kept behind a
    // ref so the effect depends only on the two primitives that gate it.
    const flushRef = useRef(() => {});
    flushRef.current = () => dispatchSnapshot(buildSnapshot());
    useEffect(() => {
        if (!pendingSend || voice.hasTranscribing) return;
        setPendingSend(false);
        flushRef.current();
    }, [pendingSend, voice.hasTranscribing]);

    const handleStopStreaming = useCallback(() => { stopFnRef.current?.(); }, []);

    // ── Reply (both User and PAI messages) ───────────────────────────────
    const handleReplyToggle = useCallback((msg) => {
        setReplyingToMessages((prev) => {
            const key = msg.dbId || msg.id;
            const exists = prev.some((m) => (m.dbId || m.id) === key);
            if (exists) return prev.filter((m) => (m.dbId || m.id) !== key);
            return [...prev, msg];
        });
    }, []);

    // ── Edit an already-sent user message → persist, truncate, RE-RUN ────
    // Mirrors the Main Office contract exactly (including the audio-management
    // panel inside <Message>, which is reused verbatim): works both after Stop
    // (before any answer) and on a message that already received one.
    const handleEditMessage = useCallback(async (messageId, dbId, newContent, voicePatch = null) => {
        if (!dbId) return;
        try {
            await apiService.editMessage(day.id, dbId, newContent, voicePatch);
            setMessages((prev) => {
                const idx = prev.findIndex((m) => m.id === messageId);
                if (idx === -1) return prev;
                const next = prev.slice(0, idx + 1);
                const patched = { ...next[idx], content: newContent, updated_at: new Date().toISOString() };
                if (voicePatch && 'voice_segments' in voicePatch) {
                    const segs = voicePatch.voice_segments;
                    patched.voiceSegments = (Array.isArray(segs) && segs.length > 0) ? segs : null;
                }
                if (voicePatch && 'voice' in voicePatch && !voicePatch.voice) patched.voice = null;
                if (voicePatch && 'typed_text' in voicePatch) patched.typedText = voicePatch.typed_text || null;
                next[idx] = patched;
                return next;
            });
            await runTurn({ backendContent: newContent, displayContent: newContent, skipUserMessage: true });
        } catch (err) {
            logger.error('CHAT', 'Bucket: error editing message', err);
        }
    }, [day.id, runTurn]);

    // Jump to a replied-to message. Scoped to THIS thread — the Main Office chat
    // stays mounted underneath, so a bare getElementById could hit its twin.
    const handleJumpToMessage = useCallback((msgId) => {
        const target = messagesRef.current.find((m) => m.id === msgId || m.dbId === msgId);
        const domId = target ? target.id : msgId;
        setHighlightedMessageId(domId);
        const el = threadRef.current?.querySelector(`#message-${CSS.escape(String(domId))}`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => setHighlightedMessageId(null), 2000);
    }, []);

    return (
        <div data-testid={`${cfg.key}-focus`} className="flex flex-col h-full min-h-0">
            {/* Single-row header — utilities in EXACT order: Search → Share → Bell → X */}
            <div className={`flex items-center border-b border-pai-hairline flex-shrink-0 min-w-0 ${
                compact ? 'gap-1 px-3 py-3' : 'gap-2 px-6 py-4'
            }`}>
                <button
                    data-testid={`${cfg.key}-focus-back`}
                    onClick={onClose}
                    aria-label={ct('Back')}
                    title={ct('Back')}
                    className="pai-icon-btn w-[38px] h-[38px] flex-shrink-0"
                >
                    <ArrowLeft size={18} />
                </button>
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <Icon size={18} className="flex-shrink-0" style={{ color: accent }} />
                    <h2 className="text-[19px] font-bold tracking-[-0.02em] text-white truncate">
                        {day.title ? `${dateLabel} · ${day.title}` : dateLabel}
                    </h2>
                </div>

                {showSearch && (
                    <input
                        data-testid={`${cfg.key}-focus-search-input`}
                        value={daySearch}
                        autoFocus
                        onChange={(e) => setDaySearch(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') runDaySearch(daySearch); }}
                        placeholder={ct('SearchInDay')}
                        className="pai-input w-44 min-w-[72px] shrink px-3 py-2 text-[14px]"
                    />
                )}
                {matchCount != null && showSearch && (
                    <span className="text-[12.5px] text-pai-meta tnum flex-shrink-0">{matchCount}</span>
                )}

                {/* 1. Search */}
                <button
                    data-testid={`${cfg.key}-focus-search`}
                    onClick={() => { setShowSearch((v) => !v); if (showSearch) { setDaySearch(''); setMatchCount(null); } }}
                    aria-label={t('auxSearchInThisDay', 'Search in this day')}
                    title={t('auxSearchInThisDay', 'Search in this day')}
                    className="pai-icon-btn w-[38px] h-[38px] flex-shrink-0"
                >
                    <Search size={18} />
                </button>
                {/* 2. Share */}
                <button
                    data-testid={`${cfg.key}-focus-share`}
                    onClick={() => onShare(day)}
                    aria-label={t('auxShareConversation', 'Share conversation')}
                    title={t('auxShareConversation', 'Share conversation')}
                    className="pai-icon-btn w-[38px] h-[38px] flex-shrink-0"
                >
                    <Share2 size={18} />
                </button>
                {/* 3. Notification Bell (reuses RemindersDropdown) */}
                <span data-testid={`${cfg.key}-focus-bell`} className="flex items-center flex-shrink-0">
                    <RemindersDropdown />
                </span>
                {/* Standard expand/collapse (Telegram dual display #444) — the same
                    control every other workspace module carries, so a day can be
                    read beside the chat or a paired feature, or expanded on demand.
                    Sits between the Bell and the X so the pinned
                    Search → Share → Bell → X order still holds. */}
                {onToggleExpand && (
                    <button
                        data-testid={`${cfg.key}-focus-expand`}
                        data-mode={mode}
                        onClick={onToggleExpand}
                        aria-label={mode === 'expanded'
                            ? t('navCollapseToSplitView', 'Collapse to split view')
                            : t('navExpandToCoverChat', 'Expand to cover chat')}
                        title={mode === 'expanded'
                            ? t('navCollapseToSplitView', 'Collapse to split view')
                            : t('navExpandToCoverChat', 'Expand to cover chat')}
                        className="pai-icon-btn w-[38px] h-[38px] flex-shrink-0"
                    >
                        {mode === 'expanded' ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                    </button>
                )}
                {/* 4. X (close → back to grid; No-Reset preserved) */}
                <button
                    data-testid={`${cfg.key}-focus-close`}
                    onClick={onClose}
                    aria-label={t('auxCloseDay', 'Close day')}
                    title={t('commonClose', 'Close')}
                    className="pai-icon-btn w-[38px] h-[38px] flex-shrink-0"
                >
                    <X size={18} />
                </button>
            </div>

            {/* Thread — the REAL chat stack. Reply arrows (User + PAI), the edit
                pencil, voice bubbles, attachments and the thinking/timer header all
                come from <Message> unchanged. */}
            <div ref={threadRef} className="flex-1 min-h-0 overflow-y-auto px-2" data-testid={`${cfg.key}-focus-thread`}>
                <ChatMessages
                    messages={messages}
                    attachedFiles={attachedFiles}
                    messagesEndRef={messagesEndRef}
                    highlightedMessageId={highlightedMessageId}
                    conversationId={day.id}
                    onEditMessage={handleEditMessage}
                    onJumpToMessage={handleJumpToMessage}
                    onReply={handleReplyToggle}
                    replyingToMessages={replyingToMessages}
                    isReadOnly={false}
                    searchQuery={showSearch ? daySearch : ''}
                    userInitials={userInitials}
                />
            </div>

            {/* Composer — the REAL <ChatInput>. Identical UI + identical
                capabilities: "+" attachments, N queued voice clips, hybrid
                text+audio in ONE send, the reply pill, and Stop mid-turn. Typing
                never blocks recording (the mic is always available). The only
                addition is `onEditSegmentTranscription`, which makes each queued
                clip's transcription editable BEFORE sending (#173's audio slice). */}
            <ChatInput
                input={input}
                setInput={setInput}
                attachedFiles={attachedFiles}
                onRemoveAttachment={handleRemoveAttachment}
                onClearAttachments={handleClearAttachments}
                onSendMessage={handleSendMessage}
                onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
                }}
                isLoading={false}
                apiStatus={apiStatus}
                canStop={sending}
                onStopStreaming={handleStopStreaming}
                replyingToMessages={replyingToMessages}
                onCancelReply={() => setReplyingToMessages([])}
                onRemoveReply={(msg) => setReplyingToMessages((prev) => prev.filter(
                    (m) => (m.dbId || m.id) !== (msg.dbId || msg.id),
                ))}
                onPasteFiles={handleAddFiles}
                isRecording={voice.isRecording}
                recordingDuration={voice.recordingDuration}
                onStartRecording={voice.start}
                onAcceptRecording={voice.accept}
                onDiscardRecording={voice.discard}
                voiceSegments={voice.segments}
                onRemoveVoiceSegment={voice.remove}
                onToggleSegmentTranscription={voice.toggleTranscription}
                onEditSegmentTranscription={voice.editTranscription}
                pendingSend={pendingSend}
                onAddFiles={handleAddFiles}
                onOpenFilePicker={() => setDocPickerOpen(true)}
            />

            {docPickerOpen && (
                <AttachPickerModal
                    onClose={() => setDocPickerOpen(false)}
                    onSelect={(docId, doc) => { handleSelectExistingDocument(docId, doc); setDocPickerOpen(false); }}
                    testIds={{
                        modal: `${cfg.key}-attach-picker`,
                        search: `${cfg.key}-attach-picker-search`,
                        item: `${cfg.key}-attach-picker-item`,
                    }}
                />
            )}
        </div>
    );
};

// ── Panel ────────────────────────────────────────────────────────────

const DayChannelPanel = ({ cfg, mode = 'split', onClose, onToggleExpand }) => {
    const { t } = useLanguage();
    const { user } = useAuth();
    const userInitials = avatarInitials(user);
    const isMobile = useIsMobile();
    // The reused <ChatInput> gates Send/Mic/"+" on a live backend (apiStatus ===
    // 'connected'), exactly as it does in the Main Office — so the bucket composer
    // obeys the same connection contract.
    const { apiStatus } = useApiStatus(apiService);
    const { Icon } = cfg;
    const accent = accentOf(cfg);

    // Channel-scoped translator: ct('Items', vars) → t('<prefix>Items', vars).
    const ct = useCallback((suffix, vars) => t(`${cfg.i18nPrefix}${suffix}`, vars), [t, cfg.i18nPrefix]);

    // ── Worlds (the Dual-Bucket System) ──────────────────────────────
    // A channel may declare several independent chronologies (Telegram:
    // Conversations vs. Tools). `cfg.worlds` absent → single-world channel
    // and every line below no-ops, so that panel is untouched.
    const worlds = cfg.worlds || null;
    const [world, setWorld] = useState(() => {
        if (!worlds) return null;
        try {
            const saved = window.localStorage.getItem(cfg.worldStorageKey);
            if (saved && worlds.some((w) => w.key === saved)) return saved;
        } catch (_) { /* private mode / storage disabled */ }
        return cfg.defaultWorld || worlds[0].key;
    });

    const [search, setSearch] = useState('');
    const [days, setDays] = useState([]);
    const [loading, setLoading] = useState(false);
    const [focusedDay, setFocusedDay] = useState(null);
    const [shareDayId, setShareDayId] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    // ── Selection model (#443) ──────────────────────────────────────
    // The File Manager "Select Mission" pattern on the Library grid: the card
    // menu's Select / Select All enter selection mode, every card shows a
    // checkbox and a click toggles it, and a red bin appears top-right once
    // something is ticked. Deleting runs ONE bulk request and exits the mode.
    const [selectMode, setSelectMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
    const [bulkError, setBulkError] = useState('');
    // Bumped on every deletion: a grid fetch issued BEFORE a delete carries a
    // pre-delete snapshot, and must never paint a just-removed card back (the
    // background poll runs every 8s, so the race is real).
    const deleteEpochRef = useRef(0);
    // In-day search term carried by a message-level deep link (?q=), handed to
    // the Focus workspace once. Cleared whenever a day is opened by hand.
    const [deepLinkQuery, setDeepLinkQuery] = useState('');

    const firstRun = useRef(true);
    // The query the grid last fetched. The debounced search effect skips a
    // fetch for the query already on screen — without it, React's dev
    // double-mount defeated the firstRun guard and fired a redundant
    // GET /days 300ms after every panel open (even after a day was focused).
    const lastQueryRef = useRef(null);
    // Per-world UI memory: search text, grid scroll offset, the open day, and the
    // last-loaded cards. The ticket's State Persistence criterion is that
    // toggling A→B→A returns to A EXACTLY as it was left — so the state is kept
    // per world rather than reset, and the cached cards repaint instantly while
    // the refetch revalidates behind them.
    const worldMemoryRef = useRef({});
    const searchRef = useRef('');
    const restoringRef = useRef(false);
    const pendingScrollRef = useRef(null);
    searchRef.current = search;
    // No-Reset rule: the grid stays mounted while a day is focused, but we also
    // explicitly capture the scroll offset and restore it when the focus view
    // closes — guaranteeing the EXACT vertical position across browsers that
    // might reset scrollTop on display:none.
    const gridScrollRef = useRef(null);
    const gridScrollTopRef = useRef(0);
    // Telegram dual display (#444): opening a day NO LONGER expands the panel.
    // It used to (a "full-screen Focus workspace"), which turned the channel into
    // a blocking overlay — the chat and any paired feature vanished the moment a
    // day opened, and the expand control was withheld while the day was open, so
    // there was no way back except leaving the day. The panel now keeps whatever
    // footprint the USER chose (split/expanded), exactly like Boards keeps its
    // footprint when a board opens, and the Focus header carries the standard
    // expand/collapse control.

    // Container-driven layout (the #142/#231 pattern — a docked split column can
    // be narrow while the VIEWPORT is wide, so a viewport breakpoint is the wrong
    // signal). `panelWidth` drives the grid's column count and the compact header.
    const panelRootRef = useRef(null);
    const [panelWidth, setPanelWidth] = useState(0);
    useLayoutEffect(() => {
        const el = panelRootRef.current;
        if (!el) return undefined;
        setPanelWidth(el.getBoundingClientRect().width);
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver((entries) => {
            const w = entries[0]?.contentRect?.width;
            if (typeof w === 'number') setPanelWidth(w);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    const compact = panelWidth > 0 && panelWidth < COMPACT_BELOW;
    const gridColumns = isMobile ? 2 : gridColumnsFor(panelWidth);

    useLayoutEffect(() => {
        if (!focusedDay && gridScrollRef.current) {
            gridScrollRef.current.scrollTop = gridScrollTopRef.current;
        }
    }, [focusedDay]);

    // Open a day into the full-screen Focus workspace. The grid stays MOUNTED
    // underneath (No-Reset), so the search string / filtered results / scrollTop
    // all survive; expanding only changes the panel's own footprint.
    const openDay = useCallback((day, { query = '' } = {}) => {
        setDeepLinkQuery(query);
        setFocusedDay(day);
        // A deep link can open a day while selection mode is on; a hidden ticked
        // set must not survive behind the Focus workspace (#443).
        setSelectMode(false);
        setSelectedIds(new Set());
    }, []);

    // Close the Focus workspace back to the Library grid. The panel's footprint
    // is the user's choice and is left alone (#444).
    const closeFocus = useCallback(() => {
        setFocusedDay(null);
    }, []);

    const fetchDays = useCallback(async (q) => {
        lastQueryRef.current = q || '';
        const epoch = deleteEpochRef.current;
        setLoading(true);
        try {
            const res = await cfg.service.listDays({
                search: q || '', ...(world ? { world } : {}),
            });
            // A delete happened while this was in flight → stale snapshot.
            if (epoch !== deleteEpochRef.current) return;
            setDays(res?.days || (Array.isArray(res) ? res : []));
        } catch (_) {
            if (epoch !== deleteEpochRef.current) return;
            setDays([]);
        } finally {
            setLoading(false);
        }
    }, [cfg.service, world]);

    // Immediate fetch on mount AND whenever the world changes (fetchDays'
    // identity carries `world`). Reads the search through a ref so a keystroke
    // doesn't re-run this — the debounced effect below owns that.
    useEffect(() => { fetchDays(searchRef.current); }, [fetchDays]);

    const debouncedFetch = useMemo(() => debounce((q) => fetchDays(q), 300), [fetchDays]);
    useEffect(() => {
        if (firstRun.current) { firstRun.current = false; return; }
        // A world switch restores the other world's search text; the effect
        // above already fetched it, so skip the duplicate debounced round trip.
        if (restoringRef.current) { restoringRef.current = false; return; }
        if ((search || '') === lastQueryRef.current) return;
        debouncedFetch(search);
        return () => debouncedFetch.cancel();
    }, [search, debouncedFetch]);

    // Switch worlds: bank the outgoing world's state, restore the incoming
    // one's, and let the fetch effect above revalidate. Cached cards paint
    // immediately, so the toggle feels instant even on a slow connection.
    const switchWorld = useCallback((next) => {
        if (!worlds || !next || next === world) return;
        worldMemoryRef.current[world] = {
            search,
            days,
            focusedDay,
            scrollTop: gridScrollTopRef.current,
        };
        try {
            window.localStorage.setItem(cfg.worldStorageKey, next);
        } catch (_) { /* private mode / storage disabled */ }

        // Selection is per-view, not banked: a ticked set from one world must
        // never be deletable from the other (#443).
        setSelectMode(false);
        setSelectedIds(new Set());
        const mem = worldMemoryRef.current[next] || {};
        restoringRef.current = true;
        pendingScrollRef.current = mem.scrollTop || 0;
        setSearch(mem.search || '');
        setDays(mem.days || []);
        setFocusedDay(mem.focusedDay || null);
        gridScrollTopRef.current = mem.scrollTop || 0;
        setWorld(next);
    }, [worlds, world, search, days, focusedDay, cfg.worldStorageKey]);

    // ── Deep link: /<channel>?day=<conversation id>[&world=…][&q=…] ──────
    // Opens one bucket straight into the Focus workspace. Same one-shot `?param`
    // contract as /boards?board=<id> (#102) and /calendar?focus_event=<id>: read
    // it, act on it, strip it — so a reload or a manual Back doesn't re-open the
    // day the user just closed.
    //
    // Today its only caller is a Telegram hit in "Search all chats"
    // ("Conversation misplaced"), which used to drag the bucket into the Main
    // Chat instead.
    const [searchParams, setSearchParams] = useSearchParams();
    const deepLinkHandledRef = useRef(null);
    useEffect(() => {
        const dayId = searchParams.get('day');
        if (!dayId || deepLinkHandledRef.current === dayId) return;

        // WORLD FIRST (the Dual-Bucket System): the two Telegram chronologies are
        // separate stores, so a bucket from the wrong one can never be found. The
        // switch re-renders with the new `world` and this effect runs again.
        const wantedWorld = searchParams.get('world');
        if (worlds && wantedWorld && wantedWorld !== world
            && worlds.some((w) => w.key === wantedWorld)) {
            switchWorld(wantedWorld);
            return;
        }

        // Claim + consume BEFORE the async hydrate below: the ref makes this
        // idempotent under StrictMode's double-invoke (which is also why nothing
        // here cancels on cleanup — the second pass would otherwise abort the
        // only in-flight fetch and the day would never open in dev).
        deepLinkHandledRef.current = dayId;
        const q = searchParams.get('q') || '';
        const next = new URLSearchParams(searchParams);
        next.delete('day');
        next.delete('world');
        next.delete('q');
        setSearchParams(next, { replace: true });

        const known = days.find((d) => d.id === dayId);
        if (known) { openDay(known, { query: q }); return; }
        // Not in the loaded page (the grid is usually still fetching on mount, and
        // the target may sit past the 60-card window or behind a filter) — hydrate
        // the card from the conversation itself. A DayBlock IS a Conversation, so
        // GET /conversations/<id> already carries channel + day_date; the Focus
        // workspace re-fetches the thread anyway.
        (async () => {
            try {
                const conv = await apiService.getConversation(dayId);
                if (!conv?.id) return;
                openDay({
                    id: conv.id,
                    day_date: conv.day_date,
                    title: conv.title,
                    title_overridden: conv.title_overridden,
                    message_count: (conv.messages || []).length,
                    updated_at: conv.updated_at,
                }, { query: q });
            } catch (_) {
                // Deleted or inaccessible — leave the user on the Library grid.
            }
        })();
    }, [searchParams, setSearchParams, worlds, world, switchWorld, days, openDay]);

    // Restore the incoming world's scroll offset once its cards are painted.
    // Ref-gated + self-consuming so it fires ONCE per switch and never fights
    // the 8s background poll (which also updates `days`).
    useLayoutEffect(() => {
        if (pendingScrollRef.current == null) return;
        if (gridScrollRef.current) {
            gridScrollRef.current.scrollTop = pendingScrollRef.current;
        }
        pendingScrollRef.current = null;
    }, [days, world]);

    // Cheap live-ish updates: refetch on window focus AND on a slow interval
    // while the library (not the focus view) is showing, so a channel message
    // ingested by the webhook appears without the user reopening the panel.
    // Both pause while a day is focused (nothing to update in the grid).
    useEffect(() => {
        if (focusedDay) return undefined;
        const refresh = () => fetchDays(search);
        const onFocus = () => refresh();
        window.addEventListener('focus', onFocus);
        const interval = setInterval(refresh, 8000);
        return () => {
            window.removeEventListener('focus', onFocus);
            clearInterval(interval);
        };
    }, [fetchDays, search, focusedDay]);

    const handleRename = useCallback(async (dayId, title) => {
        try {
            await cfg.service.renameDay(dayId, title);
            setDays((prev) => prev.map((d) => (
                d.id === dayId ? { ...d, title, title_overridden: true } : d
            )));
            setFocusedDay((f) => (f && f.id === dayId ? { ...f, title, title_overridden: true } : f));
        } catch (_) { /* ignore */ }
    }, [cfg.service]);

    const handleConfirmDelete = useCallback(async () => {
        const target = deleteTarget;
        if (!target) return;
        deleteEpochRef.current += 1;
        setDays((prev) => prev.filter((d) => d.id !== target.id));
        setDeleteTarget(null);
        if (focusedDay?.id === target.id) closeFocus();
        try { await cfg.service.deleteDay(target.id); } catch (_) { /* ignore */ }
    }, [deleteTarget, focusedDay, closeFocus, cfg.service]);

    // ── Selection (#443) ────────────────────────────────────────────
    const exitSelection = useCallback(() => {
        setSelectMode(false);
        setSelectedIds(new Set());
        setBulkConfirmOpen(false);
    }, []);

    const enterSelection = useCallback((day) => {
        setBulkError('');
        setSelectMode(true);
        setSelectedIds(new Set(day ? [day.id] : []));
    }, []);

    const selectAll = useCallback(() => {
        setBulkError('');
        setSelectMode(true);
        setSelectedIds(new Set(days.map((d) => d.id)));
    }, [days]);

    const toggleSelected = useCallback((day) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(day.id)) next.delete(day.id);
            else next.add(day.id);
            return next;
        });
    }, []);

    // Only what is ON SCREEN may stay selected: a search that filters a ticked
    // card out (or a background refresh that drops one) must never leave an
    // invisible card queued for deletion.
    useEffect(() => {
        setSelectedIds((prev) => {
            if (prev.size === 0) return prev;
            const visible = new Set(days.map((d) => d.id));
            let changed = false;
            const next = new Set();
            prev.forEach((id) => { if (visible.has(id)) next.add(id); else changed = true; });
            return changed ? next : prev;
        });
    }, [days]);

    const allSelected = days.length > 0 && days.every((d) => selectedIds.has(d.id));
    const selectedCount = selectedIds.size;

    const handleBulkDelete = useCallback(async () => {
        const ids = days.filter((d) => selectedIds.has(d.id)).map((d) => d.id);
        setBulkConfirmOpen(false);
        if (ids.length === 0) return;
        // Optimistic (the #433/#428 pattern): the cards leave on confirm; a
        // failure restores the authoritative list from the server.
        const idSet = new Set(ids);
        deleteEpochRef.current += 1;
        setDays((prev) => prev.filter((d) => !idSet.has(d.id)));
        exitSelection();
        setBulkError('');
        try {
            await apiService.bulkDeleteConversations(ids);
        } catch (_) {
            setBulkError(ct('BulkDeleteError'));
        }
        // Reconcile with the server either way (a failure brings back what
        // really survived; success confirms the removal).
        fetchDays(searchRef.current);
    }, [days, selectedIds, exitSelection, ct, fetchDays]);

    // Panel ESC / chrome-close: in focus view, return to the grid (No-Reset);
    // in selection mode, leave it; in the library, close the whole panel.
    const handleShellClose = useCallback(() => {
        if (focusedDay) closeFocus();
        else if (selectMode) exitSelection();
        else onClose?.();
    }, [focusedDay, onClose, closeFocus, selectMode, exitSelection]);

    return (
        <PanelShell
            title={t(cfg.titleKey)}
            icon={<Icon size={18} />}
            mode={mode}
            onClose={handleShellClose}
            onToggleExpand={onToggleExpand}
            noChromeHeader
        >
            <div
                ref={panelRootRef}
                data-testid={`${cfg.key}-panel`}
                data-compact={compact ? 'true' : 'false'}
                className="flex flex-col h-full min-h-0 min-w-0"
            >
                {/* Library (kept MOUNTED under the focus view → No-Reset) */}
                <div className={focusedDay ? 'hidden' : 'flex flex-col h-full min-h-0'}>
                    {/* Header row: badge + title + ● Connected · center search · expand/close */}
                    <div
                        data-testid={`${cfg.key}-library-header`}
                        className={`flex items-center border-b border-pai-hairline flex-shrink-0 ${
                            compact ? 'flex-wrap gap-2 px-4 py-3' : 'gap-3 px-6 py-4'
                        }`}
                    >
                        {/* Compact (narrow split column, #444): row 1 = identity +
                            controls, row 2 = world toggle + search. Explicit
                            `order` + a full-width spacer make the line break
                            deterministic instead of leaving it to flex-wrap. */}
                        <div className={`flex items-center min-w-0 ${compact ? 'gap-2 flex-1 order-1' : 'gap-3'}`}>
                            {/* The channel's identity: an accent-bordered badge.
                                A 1.5px border and an icon tint — never a fill. */}
                            <span
                                aria-hidden="true"
                                className={`${compact ? 'w-8 h-8' : 'w-11 h-11'} flex-none flex items-center justify-center rounded-ctl border-[1.5px]`}
                                style={{ borderColor: accent }}
                            >
                                <Icon size={compact ? 16 : 22} style={{ color: accent }} />
                            </span>
                            <span className={`${compact ? 'text-[20px]' : 'text-[28px]'} font-extrabold tracking-[-0.03em] text-white truncate min-w-0`}>{t(cfg.titleKey)}</span>
                            {/* ALWAYS green: this reports a connection state, not
                                the channel's brand colour. */}
                            <span className={`${compact ? 'hidden' : 'hidden sm:flex'} items-center gap-1.5 text-[14px] font-semibold text-pai-green flex-shrink-0`}>
                                <span className="w-2 h-2 rounded-full bg-pai-green shadow-[0_0_9px_rgba(52,208,88,0.9)]" /> {ct('Connected')}
                            </span>
                        </div>
                        {/* World toggle (Dual-Bucket System). Each world is its
                            own store, so switching refetches rather than
                            filtering — and the per-world memory above means
                            coming back lands exactly where you left. */}
                        {worlds && (
                            <div
                                data-testid="channel-world-toggle"
                                role="tablist"
                                aria-label={ct('WorldToggleLabel')}
                                // Shrinkable, not flex-none: this row also carries
                                // a 44px badge, a 28px title and the search box,
                                // and it must never overflow horizontally (#243).
                                // CSS truncation does not alter textContent, so
                                // the world labels stay exactly "PAI Tools" /
                                // "PAI Conversations" for the suite.
                                className={`flex items-center gap-1 p-1 rounded-ctl bg-white/[0.03] border border-pai-subtle min-w-0 ${
                                    compact ? 'order-4' : ''
                                }`}
                            >
                                {worlds.map((w) => {
                                    const active = world === w.key;
                                    return (
                                        <button
                                            key={w.key}
                                            type="button"
                                            role="tab"
                                            aria-selected={active}
                                            data-testid={`world-${w.key}`}
                                            data-active={active ? 'true' : 'false'}
                                            onClick={() => switchWorld(w.key)}
                                            className={`px-3.5 py-2 text-[13.5px] font-semibold rounded-seg transition-colors truncate min-w-0 ${
                                                active
                                                    ? 'bg-white/[0.1] text-white'
                                                    : 'text-pai-meta hover:text-white hover:bg-white/[0.06]'
                                            }`}
                                        >
                                            {ct(w.labelKey)}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        {compact && <div aria-hidden="true" className="order-3 basis-full h-0" />}
                        <div className={`flex justify-center ${compact ? 'order-5 flex-1 min-w-[120px]' : 'flex-1 min-w-0'}`}>
                            <div className={`relative w-full min-w-0 ${compact ? '' : 'max-w-md'}`}>
                                <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-pai-meta pointer-events-none" />
                                <input
                                    data-testid={`${cfg.key}-search`}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder={ct('SearchPlaceholder')}
                                    className="pai-input w-full h-[42px] pl-10 pr-3.5 text-[14px]"
                                />
                            </div>
                        </div>
                        <div className={`flex items-center gap-1.5 flex-shrink-0 ${compact ? 'order-2' : ''}`}>
                            {/* The red bin (#443) — top-right, and ONLY while
                                selection mode holds at least one ticked day, so a
                                stray click can never delete anything. */}
                            {selectMode && selectedCount > 0 && (
                                <button
                                    data-testid={`${cfg.key}-bulk-delete`}
                                    onClick={() => setBulkConfirmOpen(true)}
                                    aria-label={ct('BulkDeleteAction')}
                                    title={ct('BulkDeleteAction')}
                                    className="w-[38px] h-[38px] flex items-center justify-center rounded-ctl bg-pai-red text-white hover:brightness-110 transition"
                                >
                                    <Trash2 size={17} />
                                </button>
                            )}
                            {onToggleExpand && (
                                <button
                                    data-testid={`${cfg.key}-panel-expand`}
                                    data-mode={mode}
                                    onClick={onToggleExpand}
                                    className="pai-icon-btn w-[38px] h-[38px]"
                                    title={mode === 'expanded'
                                        ? t('navCollapseToSplitView', 'Collapse to split view')
                                        : t('navExpandToCoverChat', 'Expand to cover chat')}
                                >
                                    {mode === 'expanded' ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                                </button>
                            )}
                            {onClose && (
                                <button
                                    data-testid={`${cfg.key}-panel-close`}
                                    onClick={onClose}
                                    className="pai-icon-btn w-[38px] h-[38px]"
                                    title={t('auxClosePanelEsc', 'Close panel (Esc)')}
                                >
                                    <X size={18} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Grid viewport — exactly 5 per row, FILLING the panel's
                        available height (UI expansion #253).
                        It used to carry a hard `maxHeight` ceiling implementing a
                        "2 full rows + ⅓ peek" design: on any normal window that
                        sliced the third row of buckets in half and left a large
                        band of dead space below the panel, while the history was
                        stuck scrolling inside a ~382px box. The cap is gone — the
                        `flex-1 min-h-0` chain (panel → library column → this box)
                        now takes ALL the remaining vertical space, so as many
                        complete rows as fit are shown and the whole history scrolls
                        naturally inside a full-height viewport. */}
                    {selectMode && (
                        <div
                            data-testid={`${cfg.key}-selection-bar`}
                            className="flex items-center flex-wrap gap-2 px-6 py-2.5 border-b border-pai-hairline flex-shrink-0 min-w-0"
                        >
                            <span
                                data-testid={`${cfg.key}-selection-count`}
                                data-count={selectedCount}
                                className="text-[13.5px] font-semibold text-white tnum min-w-0 truncate"
                            >
                                {selectedCount > 0 ? ct('SelectionCount', { n: selectedCount }) : ct('SelectionHint')}
                            </span>
                            <div className="ml-auto flex items-center gap-1.5 flex-shrink-0">
                                <button
                                    data-testid={`${cfg.key}-select-all-toggle`}
                                    data-all={allSelected ? 'true' : 'false'}
                                    onClick={() => (allSelected ? setSelectedIds(new Set()) : selectAll())}
                                    className="pai-btn-secondary h-9 px-3 text-[13px]"
                                >
                                    {allSelected ? ct('DeselectAllAction') : ct('SelectAllAction')}
                                </button>
                                <button
                                    data-testid={`${cfg.key}-select-exit`}
                                    onClick={exitSelection}
                                    className="pai-btn-secondary h-9 px-3 text-[13px]"
                                >
                                    {ct('ExitSelection')}
                                </button>
                            </div>
                        </div>
                    )}
                    {bulkError && (
                        <div
                            data-testid={`${cfg.key}-bulk-delete-error`}
                            role="alert"
                            className="px-6 py-2 text-[13px] text-pai-red border-b border-pai-hairline flex-shrink-0"
                        >
                            {bulkError}
                        </div>
                    )}
                    <div
                        ref={gridScrollRef}
                        onScroll={(e) => { gridScrollTopRef.current = e.currentTarget.scrollTop; }}
                        className="flex-1 min-h-0 overflow-y-auto px-6 py-4"
                        data-testid={`${cfg.key}-grid-scroll`}
                    >
                        {days.length === 0 ? (
                            <div
                                className="py-16 text-center text-[14.5px] text-white/40"
                                data-testid={`${cfg.key}-empty`}
                            >
                                {loading ? '…' : (search ? ct('NoResults') : ct('NoDays'))}
                            </div>
                        ) : (
                            <div
                                data-testid={`${cfg.key}-grid`}
                                data-columns={gridColumns}
                                style={{
                                    display: 'grid',
                                    // 2-per-row on phones so the day cards stay usable
                                    // (Mobile Visual #171), container-driven otherwise
                                    // (#444): up to 5 when the panel is wide (the
                                    // library spec pins 5 on an expanded desktop
                                    // panel), fewer in a docked split column.
                                    gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
                                    gap: `${GRID_GAP}px`,
                                }}
                            >
                                {days.map((day) => (
                                    <DayCard
                                        key={day.id}
                                        cfg={cfg}
                                        day={day}
                                        dateLabel={formatDayDate(day.day_date)}
                                        onOpen={openDay}
                                        onRename={handleRename}
                                        onShare={(d) => setShareDayId(d.id)}
                                        onDelete={(d) => setDeleteTarget(d)}
                                        ct={ct}
                                        t={t}
                                        selectMode={selectMode}
                                        selected={selectedIds.has(day.id)}
                                        onToggleSelect={toggleSelected}
                                        onSelect={enterSelection}
                                        onSelectAll={selectAll}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Focus workspace */}
                {focusedDay && (
                    <FocusView
                        cfg={cfg}
                        day={focusedDay}
                        dateLabel={formatDayDate(focusedDay.day_date)}
                        onClose={closeFocus}
                        onShare={(d) => setShareDayId(d.id)}
                        userInitials={userInitials}
                        apiStatus={apiStatus}
                        ct={ct}
                        initialSearch={deepLinkQuery}
                        mode={mode}
                        onToggleExpand={onToggleExpand}
                        compact={compact}
                    />
                )}
            </div>

            {/* Share reuses the existing ShareDialog + share endpoints */}
            <ShareDialog
                isOpen={!!shareDayId}
                onClose={() => setShareDayId(null)}
                conversationId={shareDayId}
            />

            {bulkConfirmOpen && (
                <BulkDeleteModal
                    cfg={cfg}
                    count={selectedCount}
                    onConfirm={handleBulkDelete}
                    onCancel={() => setBulkConfirmOpen(false)}
                    ct={ct}
                />
            )}

            {/* Delete confirmation — EXACT ticket text + distinct Yes/No boxes */}
            {deleteTarget && (
                <DeleteDayModal
                    cfg={cfg}
                    dateLabel={formatDayDate(deleteTarget.day_date)}
                    onConfirm={handleConfirmDelete}
                    onCancel={() => setDeleteTarget(null)}
                    ct={ct}
                />
            )}
        </PanelShell>
    );
};

export default DayChannelPanel;
